import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';

import Button, { ButtonAppearance } from '../../Atoms/Button';

import { useSize } from '../../../shared/hooks/useSize';

import { DocumentType } from '../../../shared/constants/Enums';
import * as S from './ThreeEventCards.styles';

export const ThreeEventCards = ({
  children,
  documentType,
  title,
  isSlice,
  isTitleCentered,
}) => {
  if ((!title && documentType !== DocumentType.POST) || !children) {
    return null;
  }

  const screenWidth = useSize().width;
  let displayedItems = 3;

  if (screenWidth <= 1160) {
    displayedItems = 2;
  }

  if (screenWidth <= 450) {
    displayedItems = 1;
  }

  const [currentIndex, setCurrentIndex] = useState(0);
  const isLastItem = !(currentIndex < 3 - displayedItems);
  const displayedChildren = children.slice(0, 3);

  const next = () => {
    if (!isLastItem) {
      setCurrentIndex(prevState => prevState + 1);
    }
  };

  const prev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prevState => prevState - 1);
    }
  };

  if (children?.length < 3 && !isSlice) {
    return null;
  }

  const isPostSlice = documentType === DocumentType.POST;

  return (
    <S.Carousel isSlice={isSlice} isPostSlice={isPostSlice}>
      {title && (
        <S.Head
          isPostSlice={isPostSlice}
          isSlice={isSlice}
          isTitleCentered={isTitleCentered}
          displayedItems={displayedItems}
        >
          {isPostSlice && title ? (
            <S.SmallerTitle isTitleCentered={isTitleCentered}>
              {title}
            </S.SmallerTitle>
          ) : isSlice ? (
            <S.SliceTitle>{title}</S.SliceTitle>
          ) : (
            <S.Title>{title}</S.Title>
          )}
          {!isPostSlice && (
            <S.ButtonWrapper displayedItems={displayedItems}>
              <Button
                altText='previous-arrow'
                appearance={ButtonAppearance.CIRCULAR_FLOATING}
                isDisabled={currentIndex <= 0}
                onClick={() => prev()}
              >
                <FaAngleLeft />
              </Button>
              <Button
                altText='next-arrow'
                appearance={ButtonAppearance.CIRCULAR_FLOATING}
                isDisabled={isLastItem}
                onClick={() => next()}
              >
                <FaAngleRight />
              </Button>
            </S.ButtonWrapper>
          )}
        </S.Head>
      )}
      <S.ContentContainer isPostSlice={isPostSlice} isSlice={isSlice}>
        <S.List
          currentIndex={currentIndex}
          displayedItems={displayedItems}
          hasFewSlides={children?.length < 3}
          isPostSlice={isPostSlice}
        >
          {React.Children.map(displayedChildren, (child, index) => (
            <S.Item key={`${child}-${index}`}>{child}</S.Item>
          ))}
        </S.List>
      </S.ContentContainer>
    </S.Carousel>
  );
};

ThreeEventCards.propTypes = {
  /**
   * Adjust style attributes for the slice.
   */
  isSlice: PropTypes.bool,
  /**
   * List of events (must be 3) including illustration url
   * and illustration alt, event's title and event's url,
   * amount of questions and event's author name.
   */
  children: PropTypes.node.isRequired,
  /**
   * Optionnal document type in case the slice
   * comes from a specific document and needs a specific
   * design.
   */
  documentType: PropTypes.string,
  /**
   * Title of the block.
   */
  title: PropTypes.string.isRequired,
};

ThreeEventCards.defaultProps = {
  isSlice: false,
};
